import { Suspense, useEffect } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei/core/OrbitControls";
import { ACESFilmicToneMapping } from "three";
import { positionCode, samePosition } from "../domain/layout";
import { isFitted } from "../domain/vehicle";
import { AxleBeams } from "../presets/AxleBeams";
import { BodyFinish } from "./BodyFinish";
import { Bodywork } from "./Bodywork";
import { bodyPalette } from "./palettes";
import { RaycastOccluder } from "./RaycastOccluder";
import { ShadowCasters } from "./ShadowCasters";
import { StudioEnvironment } from "./StudioEnvironment";
import { Wheel } from "./Wheel";
import { statusOf, TYRE_STATES } from "../../vehicleDiagram/diagramTheme";
const MIN_POLAR_ANGLE = 0.175;
const MAX_POLAR_ANGLE = 1.484;
function VehicleScene({
  preset,
  vehicle,
  hovered,
  selected,
  chassisView = false,
  onHover,
  onSelect
}) {
  const { camera, stage, Body } = preset;
  const handleHoverEnd = (ref) => {
    if (hovered && samePosition(hovered, ref)) onHover(void 0);
  };
  useEffect(() => {
    if (!hovered) return;
    document.body.style.cursor = "pointer";
    return () => {
      document.body.style.cursor = "auto";
    };
  }, [hovered]);
  const shadowBound = stage.extent * 0.9;
  const shadowMapSize = stage.extent > 6 ? 4096 : 2048;
  const shadowNormalBias = Math.min(0.015 + stage.extent * 4e-3, 0.05);
  return <Canvas
    shadows
    dpr={[1, 2]}
    gl={{ antialias: true, alpha: true }}
    camera={{ position: camera.position, fov: camera.fov, near: 0.1, far: 100 }}
    onCreated={({ gl }) => {
      gl.toneMapping = ACESFilmicToneMapping;
      gl.toneMappingExposure = 1.05;
    }}
  >
      <ambientLight intensity={0.3} />
      <directionalLight
    castShadow
    position={[6, 9, 5]}
    intensity={1.5}
    shadow-mapSize={[shadowMapSize, shadowMapSize]}
    shadow-camera-left={-shadowBound}
    shadow-camera-right={shadowBound}
    shadow-camera-top={shadowBound}
    shadow-camera-bottom={-shadowBound}
    shadow-bias={-2e-4}
    shadow-normalBias={shadowNormalBias}
  />
      <directionalLight position={[-5, 5, -4]} intensity={0.3} />

      <Suspense fallback={null}>
        <StudioEnvironment />

        <ShadowCasters>
          <Bodywork ghosted={chassisView}>
            <RaycastOccluder enabled={!chassisView}>
              <BodyFinish silhouette={preset.silhouette}>
                <Body />
              </BodyFinish>
            </RaycastOccluder>
          </Bodywork>

          {preset.skeleton !== "none" && <AxleBeams
    wheels={preset.wheels}
    skeleton={preset.skeleton}
    finish={bodyPalette(preset.silhouette).chassis}
  />}

          {preset.wheels.map((placement) => {
    const code = positionCode(placement.ref);
    const fitment = vehicle.tyres?.[code] || null;
    return (
      <Wheel
        key={code}
        placement={placement}
        fitted={isFitted(vehicle, placement.ref)}
        statusColor={TYRE_STATES[statusOf({ current_fitment: fitment })].color}
        hovered={hovered !== void 0 && samePosition(hovered, placement.ref)}
        selected={selected !== void 0 && samePosition(selected, placement.ref)}
        onHoverStart={onHover}
        onHoverEnd={handleHoverEnd}
        onSelect={onSelect}
      />
    );
  })}
        </ShadowCasters>

        {
    /* Cast shadow lands here; the contact pass darkens where the tyres meet it. */
  }
        <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[30, 30]} />
          <shadowMaterial transparent opacity={0.26} color="#16202e" />
        </mesh>
      </Suspense>

      <OrbitControls
    target={camera.target}
    enablePan={false}
    enableDamping
    dampingFactor={0.08}
    rotateSpeed={0.8}
    zoomSpeed={0.7}
    minDistance={camera.minDistance}
    maxDistance={camera.maxDistance}
    minPolarAngle={MIN_POLAR_ANGLE}
    maxPolarAngle={MAX_POLAR_ANGLE}
  />
    </Canvas>;
}
export {
  VehicleScene
};
